import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Calendar, MapPin } from 'lucide-react';
import clsx from 'clsx';
import { api, tokenStore } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePortalSid } from './PortalLayout';
import type { Evento } from '@/lib/types';

const FAM_COLOR: Record<string, string> = {
'Conecta Folha Business': 'bg-blue/15 text-blue-light border-blue/30',
'BRM - BUY': 'bg-purple/15 text-purple border-purple/30',
'Setorial': 'bg-pink/15 text-pink border-pink/30',
'Apex / Research': 'bg-green/15 text-green border-green/30',
};

function fmtDataBox(iso?: string) {
if (!iso) return { dia: '—', mes: '' };
const d = new Date(iso);
return {
dia: String(d.getDate()).padStart(2, '0'),
mes: d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase(),
};
}

function diasAteEvento(iso?: string): number | null {
if (!iso) return null;
return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

type Filtro = 'todos' | 'meus' | 'oportunidades';

export function EventosPortal() {
const { user } = useAuth();
const portalSid = usePortalSid();
const sid = portalSid ?? user?.solicitanteId ?? '';
const [filtro, setFiltro] = useState<Filtro>('todos');
const [requested, setRequested] = useState<Set<string>>(new Set());

const eventosQ = useQuery({ queryKey: ['portal', 'eventos'], queryFn: () => api.eventos.listPortal().then(r => r.data) });
const eventos: Evento[] = useMemo(() =>
(eventosQ.data ?? []).slice().sort((a, b) => (a.dataInicio ?? '').localeCompare(b.dataInicio ?? '')),
[eventosQ.data]);

const solicitarParticipacao = useMutation({
  mutationFn: (eventoId: string) => {
    const tok = tokenStore.get();
    const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
    return fetch(`${base}/solicitacoes-evento`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
      body: JSON.stringify({ eventoId, solicitanteId: sid }),
    }).then(r => r.json());
  },
  onSuccess: (_: unknown, eventoId: string) => setRequested(prev => new Set([...prev, eventoId])),
});

const filtrados = useMemo(() => eventos.filter(e => {
const meu = (e.solicitantesIds ?? []).includes(sid);
if (filtro === 'meus') return meu;
if (filtro === 'oportunidades') return !meu;
return true;
}), [eventos, filtro, sid]);

const cMeus = eventos.filter(e => (e.solicitantesIds ?? []).includes(sid)).length;
const cOpp = eventos.length - cMeus;

return (
<div className="p-6 space-y-4">
<header className="flex items-center justify-between">
<div>
<h1 className="text-2xl font-bold text-text">Calendário Apex</h1>
<p className="text-sm text-text-2">{eventos.length} eventos · {cMeus} no seu plano · {cOpp} oportunidades</p>
</div>
<div className="flex gap-1.5">
{(['todos', 'meus', 'oportunidades'] as Filtro[]).map(f => (
<button key={f} type="button" onClick={() => setFiltro(f)}
className={clsx(
'px-3 py-1.5 text-[11.5px] font-semibold rounded border transition-colors',
filtro === f ? 'bg-blue text-white border-blue' : 'bg-panel text-text-2 border-border hover:border-border-light',
)}>
{f === 'todos' ? `Todos (${eventos.length})` :
f === 'meus' ? `Meu plano (${cMeus})` :
`Oportunidades (${cOpp})`}
</button>
))}
</div>
</header>

<div className="space-y-2">
{filtrados.length === 0 && (
<div className="bg-panel border border-border rounded-xl p-6 text-center text-muted">
Nenhum evento nessa categoria.
</div>
)}

{filtrados.map(e => {
const meu = (e.solicitantesIds ?? []).includes(sid);
const { dia, mes } = fmtDataBox(e.dataInicio);
const dias = diasAteEvento(e.dataInicio);
const futuro = dias !== null && dias >= 0;
const CardEl = meu ? Link : 'div';
const cardProps = meu ? { to: `/portal/eventos/${e.id}` } : {};
return (
<CardEl key={e.id}
{...cardProps as never}
className={clsx(
'flex items-center gap-3 p-3 rounded-lg border transition-colors',
meu ? 'bg-panel border-border hover:border-blue/50' :
'bg-gradient-to-r from-amber-50/40 to-orange-50/30 border-dashed border-orange-300 hover:border-orange-400',
)}>
<div className={clsx(
'w-14 p-1.5 rounded-md text-center shrink-0',
meu ? 'bg-gradient-to-br from-[#001B3A] to-[#1565A8] text-white' :
'bg-white border border-orange-300 text-orange-700',
!futuro && 'opacity-60',
)}>
<div className={clsx('text-lg font-extrabold leading-none', meu ? 'text-white' : 'text-orange-600')}>{dia}</div>
<div className="text-[8px] uppercase tracking-wider mt-1 opacity-80">{mes}</div>
</div>
<div className="flex-1 min-w-0">
<div className="flex items-center gap-2 mb-0.5">
<span className={clsx('text-[13px] font-bold truncate', meu ? 'text-text' : 'text-orange-900')}>{e.nome}</span>
{e.familia && (
<span className={clsx('text-[9px] font-bold px-1.5 py-0.5 rounded uppercase border', FAM_COLOR[e.familia])}>
{e.familia.replace('Conecta Folha Business', 'Conecta')}
</span>
)}
</div>
<div className="flex items-center gap-3 text-[10.5px] text-text-2">
{e.local && <span className="inline-flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{e.local}</span>}
{futuro && dias !== null && <span>em {dias} {dias === 1 ? 'dia' : 'dias'}</span>}
{!futuro && <span>realizado</span>}
</div>
</div>
<div className="shrink-0">
{meu ? (
<span className="text-[10px] font-bold uppercase text-blue-light bg-blue/10 px-2 py-1 rounded">\u2713 No seu plano</span>
) : (
<button
type="button"
className="btn-primary !py-1 !px-2.5 text-xs"
disabled={requested.has(e.id) || solicitarParticipacao.isPending}
onClick={() => solicitarParticipacao.mutate(e.id)}
>
{requested.has(e.id) ? '\u2713 Solicitado' : 'Quero participar'}
</button>
)}
</div>
</CardEl>
);
})}
</div>
</div>
);
}

void Calendar;
